import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { UserPlus, Unlock } from "lucide-react";
import { api } from "../lib/api";
import { useToast } from "../lib/toast";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Badge } from "../components/ui/Badge";

interface UserRow {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  isSupplier: boolean;
  isLocked: boolean;
  roles: string[];
}

/** Unified User Console (FR-015) — one screen for roles/vendors/plants,
 * replacing the legacy's AccountManagement/PlantApprover/RoleConfiguration
 * screen split. */
export function UserAdmin() {
  const toast = useToast();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["admin-users"], queryFn: () => api.get<{ rows: UserRow[] }>("/useradmin/users").then((r) => r.rows) });

  const unlockMutation = useMutation({
    mutationFn: (userId: number) => api.post("/useradmin/unlock", { userId }),
    onSuccess: () => {
      toast("Self-service unlock initiated");
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    },
  });

  return (
    <Card>
      <div className="p-4 flex items-center gap-2 border-b border-slate-100 dark:border-white/10">
        <span className="font-semibold text-sm">Unified User Console</span>
        <div className="ml-auto">
          <Button icon={UserPlus} onClick={() => toast("Invite sent")}>
            Invite User
          </Button>
        </div>
      </div>
      <table className="data w-full text-xs">
        <thead className="bg-slate-50 dark:bg-white/5 text-slate-500">
          <tr>
            <th className="p-3 text-left">User</th>
            <th className="p-3 text-left">Roles</th>
            <th className="p-3 text-left">Type</th>
            <th className="p-3 text-left">Status</th>
            <th className="p-3 text-left">Action</th>
          </tr>
        </thead>
        <tbody>
          {(data ?? []).map((u) => (
            <tr key={u.id} className="border-t border-slate-100 dark:border-white/5">
              <td className="p-3">
                {u.firstName} {u.lastName} <span className="text-slate-400">({u.email})</span>
              </td>
              <td className="p-3">{u.roles.join(", ") || "—"}</td>
              <td className="p-3">{u.isSupplier ? "Supplier" : "SBD Internal"}</td>
              <td className="p-3">
                <Badge tone={u.isLocked ? "red" : "green"}>{u.isLocked ? "Locked" : "Active"}</Badge>
              </td>
              <td className="p-3">
                {u.isLocked && (
                  <Button variant="ghost" icon={Unlock} onClick={() => unlockMutation.mutate(u.id)}>
                    Unlock
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}
